import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { UploadedImage } from "@/components/uploaded-image";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "@emotion/styled";
import { Uuid } from "@/domains/global/identifiers";

export const AdminBadgesViewPage: FC = () => {
  const { badgeId } = useParams<{ badgeId: Uuid }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUploadedFileId, setImageUploadedFileId] = useState<string | null>(null);

  const [initialValues, setInitialValues] = useState({
    slug: "",
    name: "",
    description: "",
    imageUploadedFileId: null as string | null,
  });

  const handleUploadImage = async () => {
    if (!imageFile) return;
    const { fileId } = await fileModule.uploadFile({ file: imageFile });
    setImageUploadedFileId(fileId);
  };

  const { data: result } = useQuery({
    queryKey: ["badge", badgeId],
    enabled: !!badgeId,
    queryFn: () =>
      api.get("/internal/v1/staff/badges/{staff_badge_id}", {
        params: { path: { staff_badge_id: badgeId! } },
      }),
  });

  useEffect(() => {
    if (result?.data) {
      const b = result.data;
      const initial = {
        slug: b.slug,
        name: b.name,
        description: b.description ?? "",
        imageUploadedFileId: b.image_uploaded_file_id ?? null,
      };
      setInitialValues(initial);
      setSlug(initial.slug);
      setName(initial.name);
      setDescription(initial.description);
      setImageUploadedFileId(initial.imageUploadedFileId);
    }
  }, [result]);

  if (!badgeId) return <div>Invalid ID</div>;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const updates: Record<string, unknown> = {};

    if (slug !== initialValues.slug) {
      updates.slug = slug;
    }

    if (name !== initialValues.name) {
      updates.name = name;
    }

    if (description !== initialValues.description) {
      updates.description = description ?? null;
    }

    if (imageUploadedFileId !== initialValues.imageUploadedFileId) {
      updates.image_uploaded_file_id = imageUploadedFileId ?? null;
    }

    if (Object.keys(updates).length === 0) {
      return;
    }

    await api.put("/internal/v1/staff/badges/{staff_badge_id}", {
      params: { path: { staff_badge_id: badgeId } },
      body: updates,
    });
    queryClient.invalidateQueries({ queryKey: ["badge", badgeId] });
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this badge?")) {
      await api.delete("/internal/v1/staff/badges/{staff_badge_id}", {
        params: { path: { staff_badge_id: badgeId } },
      });
      navigate(atlasRoutes.adminBadges({}).path);
    }
  };

  return (
    <Container>
      <h1>Edit Badge</h1>
      <Form onSubmit={handleSave}>
        <FormField>
          <Label>Slug</Label>
          <Input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            placeholder="unique-badge-slug"
            required
          />
        </FormField>

        <FormField>
          <Label>Name</Label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Badge Display Name"
            required
          />
        </FormField>

        <FormField>
          <Label>Description</Label>
          <TextArea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe what this badge represents..."
            rows={4}
          />
        </FormField>

        <FormField>
          <Label>Image File</Label>
          {imageUploadedFileId && (
            <BadgeImageWrapper>
              <UploadedImage uploadedFileId={imageUploadedFileId} size={100} />
              <ImageInfo>Current badge image</ImageInfo>
            </BadgeImageWrapper>
          )}
          <FileInput
            type="file"
            accept="image/*"
            onChange={e => setImageFile(e.target.files?.[0] ?? null)}
          />
          {imageFile && (
            <Button type="button" onClick={handleUploadImage} style={{ marginTop: "8px" }}>
              Upload Image
            </Button>
          )}
        </FormField>

        <ButtonGroup>
          <SubmitButton type="submit">Save Changes</SubmitButton>
          <DeleteButton type="button" onClick={handleDelete}>
            Delete Badge
          </DeleteButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FileInput = styled.input`
  padding: 4px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const SubmitButton = styled(Button)`
  align-self: flex-start;
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;

  &:hover {
    background-color: #c82333;
  }
`;

const BadgeImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const ImageInfo = styled.span`
  font-size: 12px;
  color: #666;
`;
